import { Alert, Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveContext } from '../api/captive';
import { IdentityPanel } from './IdentityPanel';

interface LoginFormValues {
  login: string;
  password: string;
}

/**
 * Login screen (Amendment 02 Part A). The subscriber authenticates with his
 * EXISTING billing login + password; no account creation, no device-entry form.
 * On success the parent flow immediately runs the automatic device-registration
 * step. The connecting device's identity (from /captive/context) is shown for
 * reassurance when available; when that best-effort read-out came back empty the
 * panel degrades to a muted hint — login and registration stay available, because
 * the core identifies the device from the captive lease of the connection itself.
 *
 * ⚠ THE PANEL AND THE HINT ARE NO LONGER EXCLUSIVE *(2026-08-22, the customer's
 * request)*. `/captive/context` now answers the caller's own ADDRESS even when it
 * holds no lease, so the ordinary case for a subscriber an operator is phoning about
 * is «address known, device unknown»: the panel renders with its one address row AND
 * the muted hint appears below it, saying the device is not yet identified. They used
 * to be an either/or because a context either resolved wholly or 404'd.
 */
export function LoginScreen({
  context,
  identityUnavailable,
  submitting,
  error,
  onSubmit,
}: {
  context: CaptiveContext | null;
  /**
   * The DEVICE could not be identified — keyed on an empty `context.mac`, not on a
   * null context, because a context carrying only `client_ip` is now a success (see
   * the note above). It shows the muted hint beside whatever the panel could render.
   */
  identityUnavailable: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: (login: string, password: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <p className="portal-subtitle">{t('login.subtitle')}</p>
      {context && <IdentityPanel context={context} />}
      {identityUnavailable && <p className="portal-hint">{t('connection.none')}</p>}
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} role="alert" />
      )}
      <Form<LoginFormValues>
        layout="vertical"
        requiredMark={false}
        disabled={submitting}
        onFinish={(v) => onSubmit(v.login, v.password ?? '')}
      >
        <Form.Item
          name="login"
          label={t('login.loginLabel')}
          rules={[{ required: true, message: t('login.loginRequired') }]}
        >
          <Input autoFocus autoComplete="username" className="mono" />
        </Form.Item>
        <Form.Item
          name="password"
          label={t('login.passwordLabel')}
          rules={[{ required: true, message: t('login.passwordRequired') }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            {t('login.submit')}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}
